import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MapPin, CalendarDays, ShieldCheck, ArrowLeft, FileWarning, Map as MapIcon } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { api } from '../lib/api';
import { PageHeader, StatusBadge, SectionHeader, formatDate, categoryLabel, STATUS_LABELS } from '../components/Shared';
import { PinBadges } from '../components/TrackerShared';
import { PhotoGrid } from '../components/PhotoLightbox';
import { SharePanel } from '../components/SharePanel';
import { MapContainer, TileLayer, Marker, Circle } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { pushpinIcon } from '../components/MapPushpin';

export default function PublicIssue() {
  const { trackingNumber } = useParams();
  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    api.get(`/issues/public/${encodeURIComponent(trackingNumber)}`)
      .then((r) => setIssue(r.data))
      .catch((err) => setError(err.response?.data?.detail || 'No public report found with that tracking number.'))
      .finally(() => setLoading(false));
  }, [trackingNumber]);

  if (loading) {
    return (
      <div>
        <PageHeader kicker="WARD 8 PUBLIC ISSUE TRACKER APP (PITA)" title="Public Issue Report" subtitle="Report it. Map it. Track it. Get answers." />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="public-issue-loading">
          <Skeleton className="h-8 w-2/3 mb-4" />
          <Skeleton className="h-32 w-full mb-4" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  if (error || !issue) {
    return (
      <div>
        <PageHeader kicker="WARD 8 PUBLIC ISSUE TRACKER APP (PITA)" title="Public Issue Report" subtitle="Report it. Map it. Track it. Get answers." />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <Card><CardContent className="p-6 sm:p-8 text-center" data-testid="public-issue-error">
            <p className="font-serif font-bold text-[#1a2744] text-lg">Report not found</p>
            <p className="text-sm text-slate-600 mt-2">{error || 'This report may be private or the tracking number may be incorrect.'}</p>
            <div className="flex flex-wrap justify-center gap-3 mt-5">
              <Button asChild variant="outline" className="h-11 font-bold btn-press" data-testid="public-issue-back-map"><Link to="/map"><MapIcon className="h-4 w-4 mr-1.5" /> View the Public Map</Link></Button>
              <Button asChild className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press"><Link to="/issue-tracker">Ward 8 Issue Tracker App (PITA)</Link></Button>
            </div>
          </CardContent></Card>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader kicker="WARD 8 PUBLIC ISSUE TRACKER APP (PITA)" title={issue.title || 'Public Issue Report'} subtitle={`Public report ${issue.tracking_number}. Report it. Map it. Track it. Get answers.`} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10" data-testid="public-issue-page">
        <Link to="/map" className="inline-flex items-center gap-1 text-sm font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4 mb-4" data-testid="public-issue-back-link">
          <ArrowLeft className="h-4 w-4" /> Back to the Public Map
        </Link>

        {/* Privacy notice */}
        <span className="flex w-fit items-center gap-1.5 text-xs font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5 mb-4" data-testid="public-issue-privacy-badge">
          <ShieldCheck className="h-3.5 w-3.5" /> Public-safe view: no names, no exact addresses, no private details
        </span>

        <Card data-testid="public-issue-card">
          <CardContent className="p-5 sm:p-7">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="font-mono text-sm font-bold text-[#C9A84C]" data-testid="public-issue-tracking-number">{issue.tracking_number}</span>
              <StatusBadge status={issue.status} />
            </div>
            <h2 className="font-serif font-black text-[#1a2744] text-xl sm:text-2xl mt-2 leading-snug" data-testid="public-issue-title">{issue.title}</h2>
            {issue.public_summary && <p className="text-slate-600 mt-2 leading-relaxed break-words" data-testid="public-issue-summary">{issue.public_summary}</p>}

            <PinBadges pin={issue} />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3" data-testid="public-issue-category">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Category</p>
                <p className="font-semibold text-[#1a2744] text-sm mt-0.5">{categoryLabel(issue.category)}</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3" data-testid="public-issue-area">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">General area</p>
                <p className="font-semibold text-[#1a2744] text-sm mt-0.5 flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-[#C9A84C]" /> {issue.area || 'Ward 8 (generalized)'}</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3" data-testid="public-issue-date">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Reported</p>
                <p className="font-semibold text-[#1a2744] text-sm mt-0.5 flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5 text-[#C9A84C]" /> {formatDate(issue.date_reported)}</p>
              </div>
            </div>

            {/* Approved public photos: click to enlarge */}
            {issue.photos?.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Photos approved for public display ({issue.photos.length})</p>
                <PhotoGrid photos={issue.photos} caption={issue.tracking_number} testId="public-issue-photos" />
              </div>
            )}

            {/* Privacy-safe map pin */}
            {issue.pin && (
              <div className="mt-5" data-testid="public-issue-map">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Where it is{issue.pin.exact_public ? '' : ' (approximate, to protect privacy)'}</p>
                <div className="h-56 sm:h-64 rounded-xl overflow-hidden border border-slate-200">
                  <MapContainer center={[issue.pin.lat, issue.pin.lng]} zoom={issue.pin.exact_public ? 16 : 14} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false} dragging={true}>
                    <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    {!issue.pin.exact_public && <Circle center={[issue.pin.lat, issue.pin.lng]} radius={150} pathOptions={{ color: '#1a2744', fillColor: '#1a2744', fillOpacity: 0.08, weight: 1 }} />}
                    <Marker position={[issue.pin.lat, issue.pin.lng]} icon={pushpinIcon('#b91c1c')} />
                  </MapContainer>
                </div>
                <div className="mt-2"><Link to={`/map?track=${issue.tracking_number}`} className="text-xs font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-2" data-testid="public-issue-open-map">Open on the full public map</Link></div>
              </div>
            )}

            {/* Share: permanent public link with social preview */}
            <div className="mt-5 pt-4 border-t border-slate-100">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Share this public report</p>
              <SharePanel report={issue} />
            </div>
          </CardContent>
        </Card>

        {/* Public timeline */}
        <div className="mt-8">
          <SectionHeader kicker="FOLLOW-UP ON RECORD" title="Public status timeline" dek="Every public update on this report, so follow-up is visible, not forgotten." />
          {issue.updates?.length === 0 ? (
            <Card><CardContent className="p-5 text-sm text-slate-500" data-testid="public-issue-timeline-empty">No public updates yet. Updates appear here as the report moves forward.</CardContent></Card>
          ) : (
            <div className="space-y-3" data-testid="public-issue-timeline">
              {issue.updates.map((u) => (
                <Card key={u.id} data-testid="public-issue-update">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <StatusBadge status={u.status} />
                      <span className="text-xs text-slate-500">{formatDate(u.date)}</span>
                    </div>
                    <p className="text-sm text-slate-700 mt-2 leading-relaxed break-words">{u.note}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="mt-8 bg-[#1a2744] rounded-2xl p-6 sm:p-8" data-testid="public-issue-cta">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#C9A84C] mb-2">SEEING THE SAME PROBLEM?</p>
          <p className="text-white font-serif font-bold text-lg leading-relaxed">One complaint can be overlooked. A map full of complaints cannot.</p>
          <div className="flex flex-wrap gap-3 mt-4">
            <Button asChild className="h-12 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press" data-testid="report-similar-issue-btn">
              <Link to={`/issue-tracker/report?category=${issue.category}`}><FileWarning className="h-4 w-4 mr-1.5" /> Report a similar issue</Link>
            </Button>
            <Button asChild variant="outline" className="h-12 border-white bg-transparent text-white hover:bg-white/10 font-bold btn-press" data-testid="public-issue-view-map-btn">
              <Link to="/map"><MapIcon className="h-4 w-4 mr-1.5" /> View the Public Map</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
